import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import {
  PROPERTY_CATEGORIES,
  PROPERTY_CATEGORIES_DATA,
  PropertyCategory,
  PropertyCategoryData,
  PropertyType,
} from "../constants/propertyConstants";
import { SearchService } from "../services/search.service";

/**
 * Hook for working with property categories and subcategories
 * @param selectedCategory - selected property category
 * @param namespace - translation namespace, default is "PropertyType"
 * @returns object with categories and subcategories data
 */
export const usePropertyCategories = (
  selectedCategory?: string,
  namespace = "PropertyType",
) => {
  const t = useTranslations("PropertyType");

  // Property type data request from server
  const {
    data: serverData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["showPropertyType"],
    queryFn: () => SearchService.showPropertyType(),
  });

  // Main property categories list (static fallback)
  const staticCategories = useMemo<PropertyCategory[]>(
    () =>
      PROPERTY_CATEGORIES.map((category) => ({
        value: category.value,
        label: t(category.value),
      })),
    [t],
  );

  // Categories data with subcategories (static fallback)
  const staticCategoriesData = useMemo<
    Record<string, PropertyCategoryData>
  >(() => {
    const result: Record<string, PropertyCategoryData> = {};

    Object.entries(PROPERTY_CATEGORIES_DATA).forEach(([key, data]) => {
      result[key] = {
        categoryName: t(key),
        subcategories: data.subcategories.map((subcategory) => ({
          value: subcategory.value,
          label: t(subcategory.value),
        })),
      };
    });

    return result;
  }, [t]);

  // Convert server data to the format used in the hook
  const categories = useMemo<PropertyCategory[]>(() => {
    if (!serverData) {
      return staticCategories;
    }

    return Object.keys(serverData).map((key) => ({
      value: key,
      label: t(key),
    }));
  }, [serverData, staticCategories, t]);

  // Convert server data to PropertyCategoryData format
  const categoriesData = useMemo<Record<string, PropertyCategoryData>>(() => {
    if (!serverData) {
      return staticCategoriesData;
    }

    return Object.entries(serverData).reduce(
      (acc, [category, subcategories]) => {
        acc[category] = {
          categoryName: t(category),
          subcategories: (subcategories as string[]).map((subcategory) => ({
            value: subcategory,
            label: t(subcategory),
          })),
        };
        return acc;
      },
      {} as Record<string, PropertyCategoryData>,
    );
  }, [serverData, staticCategoriesData, t]);

  // Convert data to propertyTypes format (used in previous usePropertyType hook)
  const propertyTypes = useMemo<PropertyType[]>(
    () =>
      categoriesData
        ? Object.entries(categoriesData).map(([category, data]) => ({
            category,
            categoryName: data.categoryName,
            subcategories: data.subcategories.map((item) => item.value),
            subcategoriesName: data.subcategories.map((item) => item.label),
          }))
        : [],
    [categoriesData],
  );

  // Get selected category data
  const selectedCategoryData = useMemo(
    () => (selectedCategory ? categoriesData[selectedCategory] : undefined),
    [selectedCategory, categoriesData],
  );

  // Get subcategories for selected category
  const subcategories = useMemo(
    () => selectedCategoryData?.subcategories || [],
    [selectedCategoryData],
  );

  return {
    categories,
    subcategories,
    selectedCategoryData,
    propertyTypes,
    isLoading,
    error,
    refetch,
  };
};
